/**
 * Pokemon Card Marquee Animation
 * Creates an infinite scrolling marquee of Pokemon cards along an SVG path
 */

class CardMarquee {
    constructor(container) {
        this.container = container;
        this.cards = [];
        this.path = null;
        this.pathLength = 0;
        this.speed = 0.5; // Speed of animation
        this.cardCount = 12;
        this.cardSpacing = 1 / this.cardCount;
        this.isPlaying = true;
        
        this.init();
    }
    
    init() {
        this.setupPath();
        this.createCards();
        this.animate();
        this.setupInteractions();
    }
    
    setupPath() {
        // Define the path for cards to follow
        // Wave pattern across the screen
        const width = window.innerWidth;
        const height = 400;
        const amplitude = 100;
        const frequency = 3;
        
        // Create SVG path string
        let pathData = `M 0,${height/2}`;
        const steps = 50;
        
        for (let i = 1; i <= steps; i++) {
            const x = (width / steps) * i;
            const y = height/2 + Math.sin((i / steps) * Math.PI * frequency) * amplitude;
            pathData += ` L ${x},${y}`;
        }
        
        this.pathString = pathData;
        
        // Create path element for measurement
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', this.pathString);
        svg.appendChild(path);
        document.body.appendChild(svg);
        this.pathLength = path.getTotalLength();
        document.body.removeChild(svg);
    }
    
    createCards() {
        const cardsContainer = this.container.querySelector('.marquee-cards');
        if (!cardsContainer) return;
        
        // Clear existing cards
        cardsContainer.innerHTML = '';
        
        // Create cards with staggered positions
        for (let i = 0; i < this.cardCount; i++) {
            const card = document.createElement('div');
            card.className = 'marquee-card';
            card.dataset.index = i;
            card.dataset.progress = i * this.cardSpacing;
            
            const img = document.createElement('img');
            img.src = '/images/pokemon-zard-front.gif';
            img.alt = `Pokemon Card ${i + 1}`;
            img.loading = 'lazy';
            
            card.appendChild(img);
            cardsContainer.appendChild(card);
            
            this.cards.push({
                element: card,
                progress: i * this.cardSpacing,
                scale: 1,
                rotation: 0
            });
        }
    }
    
    getPointAtLength(length) {
        // Calculate point on path at given length
        // This is a simplified version - in production, use SVG's getPointAtLength
        const progress = length / this.pathLength;
        const width = window.innerWidth;
        const height = 400;
        const amplitude = 100;
        const frequency = 3;
        
        const x = width * progress;
        const y = height/2 + Math.sin(progress * Math.PI * frequency) * amplitude;
        
        return { x, y };
    }
    
    updateCard(card, deltaTime) {
        // Update card progress
        card.progress += this.speed * deltaTime * 0.001;
        
        // Loop back to start
        if (card.progress > 1) {
            card.progress -= 1;
        }
        
        // Get position on path
        const length = card.progress * this.pathLength;
        const point = this.getPointAtLength(length);
        
        // Calculate rotation based on path direction
        const nextPoint = this.getPointAtLength(length + 10);
        const angle = Math.atan2(nextPoint.y - point.y, nextPoint.x - point.x) * 180 / Math.PI;
        
        // Apply transforms
        card.element.style.transform = `
            translate(${point.x}px, ${point.y}px) 
            rotate(${angle}deg)
            scale(${card.scale})
        `;
        
        // Update z-index based on y position (cards higher up appear behind)
        card.element.style.zIndex = Math.floor(1000 - point.y);
    }
    
    animate() {
        let lastTime = performance.now();
        
        const frame = (currentTime) => {
            if (!this.isPlaying) {
                requestAnimationFrame(frame);
                return;
            }
            
            const deltaTime = currentTime - lastTime;
            lastTime = currentTime;
            
            // Update all cards
            this.cards.forEach(card => {
                this.updateCard(card, deltaTime);
            });
            
            requestAnimationFrame(frame);
        };
        
        requestAnimationFrame(frame);
    }
    
    setupInteractions() {
        // Pause on hover
        this.container.addEventListener('mouseenter', () => {
            this.isPlaying = false;
        });
        
        this.container.addEventListener('mouseleave', () => {
            this.isPlaying = true;
        });
        
        // Card hover effects
        this.cards.forEach(card => {
            card.element.addEventListener('mouseenter', () => {
                card.scale = 1.2;
                card.element.style.transition = 'transform 0.3s ease';
            });
            
            card.element.addEventListener('mouseleave', () => {
                card.scale = 1;
                card.element.style.transition = '';
            });
        });
        
        // Responsive handling
        window.addEventListener('resize', () => {
            this.setupPath();
        });
    }
    
    // Public methods
    play() {
        this.isPlaying = true;
    }
    
    pause() {
        this.isPlaying = false;
    }
    
    setSpeed(speed) {
        this.speed = speed;
    }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
    const marqueeContainers = document.querySelectorAll('.card-marquee-section');
    
    marqueeContainers.forEach(container => {
        // Only initialize if not using CSS animation
        if (!container.classList.contains('css-only')) {
            new CardMarquee(container);
        }
    });
});

// Export for use in other modules
export default CardMarquee;